import type {
  AirTrackingResponse,
  Coordinate,
  FollowShipmentPayload,
  ShipmentStatus,
  TrackingLocation,
  TrackingResponse,
  TrackingRoutePoint,
} from "api";

/**
 * Reading a gateway search result. The tracking widget does the same work in
 * `SearchApiParser`, but that lives behind its own model layer and pulls in
 * the whole card entity; the cabinet needs six fields and the route geometry.
 */

const locationsOf = (response: TrackingResponse): TrackingLocation[] => response.data?.locations ?? [];

const locationById = (response: TrackingResponse, id: number | undefined): TrackingLocation | null =>
  id === undefined ? null : (locationsOf(response).find((location) => location.id === id) ?? null);

/** "Rotterdam, NL" — the form the saved card stores and the table shows. */
const locationName = (location: TrackingLocation | null): string | null => {
  if (location === null) return null;
  if (location.name && location.country?.code) return `${location.name}, ${location.country.code}`;

  return location.name ?? location.country?.code ?? null;
};

/** Splits a route point into the actual/estimated pair the card stores. */
const splitDate = (point: TrackingRoutePoint | undefined): { value: string | null; actual: boolean } => ({
  value: point?.datetime ?? null,
  actual: point?.is_actual === true,
});

const containersOf = (response: TrackingResponse) => response.data?.containers ?? [];

/**
 * Container types counted the way the tracking widget counts them, so a card
 * followed here and one followed in the widget read identically.
 */
const sizeTypes = (response: TrackingResponse): string[] => {
  const counts = new Map<string, number>();

  containersOf(response).forEach((container) => {
    if (!container.type) return;

    counts.set(container.type, (counts.get(container.type) ?? 0) + 1);
  });

  return [...counts.entries()].sort(([, a], [, b]) => b - a).map(([type, count]) => `${count} x ${type}`);
};

/** Where the shipment is now, as the gateway last predicted it. */
export const currentPosition = (response: TrackingResponse): Coordinate | null =>
  containersOf(response)[0]?.route_details?.predicted_position?.coordinates ?? null;

/**
 * True when the gateway answered but found nothing to track. A failed lookup
 * still comes back with a `shipment` object — every field inside it null — so
 * the reference number, not the object, is what says whether there is a card.
 */
export const isEmptyResult = (response: TrackingResponse): boolean =>
  response.success !== true || !response.data?.shipment?.reference_number;

/** The back office sets this instead of a shipment once the plan is spent. */
export const isLimitReached = (response: TrackingResponse): boolean => response.requestLimitReachedWebApp === true;

/**
 * Why the gateway found nothing, in words the customer can act on. Anything
 * not listed keeps the generic "check the number" wording — the gateway has
 * two dozen codes and most of them mean the same thing to a customer.
 */
const SEARCH_REASONS: Record<string, string> = {
  WRONG_REFERENCE_NUMBER: "That number does not look like one this carrier issues.",
  WRONG_REFERENCE_TYPE: "That number is not of the kind you picked.",
  WRONG_CARRIER_CODE: "That carrier is not one we can look up.",
  AUTO_CANT_DETECT_CARRIER: "We could not tell which carrier this number belongs to — pick one and try again.",
  AUTO_CANT_FIND_INFO: "No carrier we asked has anything on this number yet.",
  CARRIER_NOT_SUPPORTED: "We do not track this carrier yet.",
  CARRIER_NOT_SUPPORT_REF_TYPE: "This carrier cannot be searched by that kind of number.",
  CARRIER_TEMPORARILY_DISABLED: "This carrier is temporarily unavailable. Please try again later.",
  CARRIER_UNDER_MAINTENANCE: "This carrier is under maintenance. Please try again later.",
  CARRIER_NO_RESPONSE: "The carrier did not answer. Please try again in a few minutes.",
  CARRIER_SHIPMENT_CANCELED: "The carrier has cancelled this shipment.",
};

export const searchFailureReason = (response: { status_code?: string }): string | null =>
  SEARCH_REASONS[response.status_code ?? ""] ?? null;

/**
 * Turns a search result into the body of a follow. `shipment_type` is not in
 * the response — sea and air share one saved list, so the caller passes the
 * mode it searched in.
 */
export const toFollowPayload = (
  response: TrackingResponse,
  shipmentType: FollowShipmentPayload["shipment_type"]
): FollowShipmentPayload | null => {
  const shipment = response.data?.shipment;

  if (shipment === undefined) return null;

  const route = response.data?.route;
  const departure = splitDate(route?.first_port_of_loading?.departure);
  const arrival = splitDate(route?.final_port_of_discharge?.arrival);
  const types = sizeTypes(response);

  return {
    reference: shipment.reference_number,
    shipment_type: shipmentType,
    type: shipment.reference_type,
    carrier_scac: shipment.carrier?.code ?? null,
    carrier_name: shipment.carrier?.name ?? null,
    status: shipment.status ?? "UNKNOWN",
    equipments_count: containersOf(response).length,
    size_type: types.length === 0 ? null : JSON.stringify(types),
    location: {
      from: locationName(locationById(response, route?.place_of_receipt?.location_id)),
      to: locationName(locationById(response, route?.final_port_of_discharge?.location_id)),
      transport: shipmentType === "AIR" ? "AIR" : "SEA",
      pin: currentPosition(response),
    },
    date: {
      from: departure.value,
      from_actual: departure.actual,
      to: arrival.value,
      to_actual: arrival.actual,
      to_predictive: null,
    },
  };
};

/* ---- air ---- */

const KNOWN_STATUSES: ShipmentStatus[] = ["UNKNOWN", "PLANNED", "IN_TRANSIT", "DELIVERED", "CANCELLED"];

/**
 * The air gateway types `status` as a bare string. Only the values the saved
 * card understands are kept; anything else is stored as unknown rather than
 * written through and left to break the table's status logic.
 */
const knownStatus = (status: string | undefined): ShipmentStatus =>
  KNOWN_STATUSES.find((known) => known === status) ?? "UNKNOWN";

/** "Frankfurt, DE" from the airport id the schedule points refer to. */
const airportName = (response: AirTrackingResponse, id: string | undefined): string | null => {
  const airport = response.data?.locations?.find((location) => location.id === id);

  if (airport === undefined) return null;

  return airport.country?.code ? `${airport.name}, ${airport.country.code}` : airport.name;
};

/** True when the air gateway answered but found no waybill. */
export const isEmptyAirResult = (response: AirTrackingResponse): boolean => response.data?.shipment === undefined;

/**
 * The air equivalent of `toFollowPayload`. The air gateway sends a flat
 * schedule rather than a route, so the estimated/actual split comes from two
 * fields instead of an `is_actual` flag.
 */
export const toAirFollowPayload = (response: AirTrackingResponse): FollowShipmentPayload | null => {
  const shipment = response.data?.shipment;

  if (shipment === undefined) return null;

  const airline = response.meta?.airline ?? null;
  const cargo = shipment.cargo_details ?? null;

  // The same "N unit" strings the air widget writes, so both read alike.
  const size = [cargo?.quantity ? `${cargo.quantity} pcs` : null, cargo?.weight ? `${cargo.weight} kg` : null].filter(
    (part): part is string => part !== null
  );

  return {
    reference: shipment.awb_number,
    shipment_type: "AIR",
    type: "AWB",
    carrier_scac: airline?.iata_code ?? null,
    carrier_name: airline?.name ?? null,
    status: knownStatus(shipment.status),
    equipments_count: cargo?.quantity ?? 0,
    size_type: size.length === 0 ? null : JSON.stringify(size),
    location: {
      from: airportName(response, shipment.departure?.location),
      to: airportName(response, shipment.arrival?.location),
      transport: "AIR",
      pin: null,
    },
    date: {
      from: shipment.departure?.actual ?? shipment.departure?.estimated ?? null,
      from_actual: shipment.departure?.actual != null,
      to: shipment.arrival?.actual ?? shipment.arrival?.estimated ?? null,
      to_actual: shipment.arrival?.actual != null,
      to_predictive: null,
    },
  };
};
